import { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const timeOf = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export default function RoomChat({ code, userId, messages, setMessages, connected }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  // Live delivery comes over the room WebSocket; poll only while it is down.
  useEffect(() => {
    if (connected) return;
    const t = setInterval(async () => {
      const last = messages[messages.length - 1]?.created_at;
      try {
        const { data } = await api.get(`/rooms/${code}/messages`, { params: last ? { after: last } : {} });
        if (data.messages?.length) setMessages((m) => mergeMessages(m, data.messages));
      } catch {}
    }, 4000);
    return () => clearInterval(t);
  }, [connected, code, messages, setMessages]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/rooms/${code}/messages`, { text: t });
      setMessages((m) => mergeMessages(m, [data]));
      setText("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Message not sent");
    } finally { setSending(false); }
  };

  return (
    <div className="surface p-4 flex flex-col" style={{ height: "min(60vh, 520px)" }} data-testid="room-chat">
      <div className="flex-1 overflow-y-auto space-y-3 pr-1" data-testid="room-chat-messages">
        {messages.length === 0 && <p className="text-center text-[13px] text-[#8E8B99] py-10">No messages yet — say hi to your study group.</p>}
        {messages.map((m) => {
          const mine = m.user_id === userId;
          return (
            <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
              <span className="text-[10.5px] text-[#8E8B99] mb-0.5">{mine ? "You" : m.name} · {timeOf(m.created_at)}</span>
              <span className={`px-3 py-2 rounded-2xl text-[13.5px] max-w-[80%] break-words ${mine ? "bg-[#7C5CFF] text-white" : "bg-white/[0.06]"}`}>{m.text}</span>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 mt-3">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={500} placeholder="Message the room"
          aria-label="Chat message" className="input flex-1" data-testid="room-chat-input" />
        <button type="submit" disabled={sending || !text.trim()} className="btn btn-primary px-4" aria-label="Send" data-testid="room-chat-send">
          <Send size={15} />
        </button>
      </form>
    </div>
  );
}

export function mergeMessages(prev, incoming) {
  const seen = new Set(prev.map((m) => m.id));
  return [...prev, ...incoming.filter((m) => !seen.has(m.id))];
}
